import React, { useEffect } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Flex, Icon } from 'design-system-web';
import { WatchlistSeverityLevel, WatchlistLevelConfig } from 'graphql-query-contracts';
import { PillColor } from 'application-types';
import { colors } from 'constants-colors';
import { formatWatchlistSeverityLevel } from '../../helpers/formatWatchlistSeverityLevel';
import { useWatchlistLevelConfigs } from '../../hooks/useWatchlistLevelConfigs';
import useChannelGroupRolesQuery from 'lane-shared/hooks/useChannelGroupRolesQuery';
import { convertToUUID, compareUUIDs } from 'uuid-encoding';
import Pill from 'components/general/Pill';

import styles from './WatchlistLevelSelector.scss';

const SEVERITY_ORDER = [
  WatchlistSeverityLevel.WatchlistSeverityLevelHigh,
  WatchlistSeverityLevel.WatchlistSeverityLevelMedium,
  WatchlistSeverityLevel.WatchlistSeverityLevelLow,
];

const PILL_COLORS: Record<WatchlistSeverityLevel, PillColor> = {
  [WatchlistSeverityLevel.WatchlistSeverityLevelHigh]: 'red',
  [WatchlistSeverityLevel.WatchlistSeverityLevelMedium]: 'honey',
  [WatchlistSeverityLevel.WatchlistSeverityLevelLow]: 'gray',
  [WatchlistSeverityLevel.WatchlistSeverityLevelInvalid]: 'gray',
};

const getIconColor = (pillColor: PillColor) => {
  switch (pillColor) {
    case 'red':
      return colors.errorDark;
    case 'honey':
      return colors.colorYellowSecondary;
    case 'gray':
      return colors.colorGraySecondary;
    default:
      return colors.black;
  }
};

export type WatchlistLevelSelectorProps = {
  className?: string;
  channelId: string;
  value: WatchlistSeverityLevel;
  selectedConfigId?: string;
  onChange: (level: WatchlistSeverityLevel, configId?: string) => void;
};

export function WatchlistLevelSelector({
  className,
  channelId,
  value,
  selectedConfigId,
  onChange,
}: WatchlistLevelSelectorProps): React.ReactElement {
  const { t } = useTranslation();
  const convertedChannelId = channelId ? convertToUUID(channelId) : '';
  const { data: configsFromHook } = useWatchlistLevelConfigs(channelId);
  const { groupRoles } = useChannelGroupRolesQuery({
    channelId: convertedChannelId,
    includeWorkplaceMember: false,
  });
  useEffect(() => {
    if (configsFromHook && configsFromHook.length > 0 && !selectedConfigId) {
      const matchingConfig = configsFromHook.find(config => config.level === value);
      
      if (matchingConfig?.id) {
        onChange(value, matchingConfig.id);
      }
    }
  }, [configsFromHook, selectedConfigId, value, onChange]);

  const getTeamNames = (targetIds: string[]): string[] => {
    return targetIds
      .map(id => {
        const role = groupRoles.find(role => compareUUIDs(role._id, id));

        return role?.name;
      })
      .filter((name): name is string => Boolean(name));
  };

  const getConfigForLevel = (level: WatchlistSeverityLevel): WatchlistLevelConfig | undefined => {
    return configsFromHook?.find(config => config.level === level);
  };

  const renderLevelCard = (level: WatchlistSeverityLevel) => {
    const config = getConfigForLevel(level);
    const isSelected = value === level;
    const pillColor = PILL_COLORS[level];
    const iconColor = getIconColor(pillColor);
    const levelName = formatWatchlistSeverityLevel(level, t);
    
    const emailNotificationsEnabled = config?.isNotificationsEnabled ?? false;
    const emailRecipients = emailNotificationsEnabled && config?.notificationTargets
      ? getTeamNames(config.notificationTargets)
      : [];
    const isSystemLoggingEnabled = config?.isWatchlistDisplayed ?? true;
    const isManualApprovalEnabled = config?.isApprovalRequired ?? false;

    return (
      <div
        key={level}
        className={cx(styles.levelCard, { [styles.selected]: isSelected })}
        onClick={() => onChange(level, config?.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onChange(level, config?.id);
          }
        }}
      >
        <div className={styles.cardHeader}>
          <div className={styles.radioButton}>
            {isSelected && (
              <Icon name="check" className={styles.checkIcon} />
            )}
          </div>
          <div className={styles.pillContainer}>
            <Pill
              text={levelName}
              color={pillColor}
              size="medium"
              iconName="circle"
              iconColor={iconColor}
            />
          </div>
        </div>
        
        <div className={styles.cardContent}>
          <div className={styles.settingRow}>
            <span className={styles.settingLabel}>
              {t('web.admin.visitorManagement.configuration.watchlistLevelSelector.emailNotifications')}
            </span>
            <span className={styles.settingValue}>
              {emailNotificationsEnabled ? t('web.admin.visitorManagement.configuration.watchlistLevelSelector.on') : t('web.admin.visitorManagement.configuration.watchlistLevelSelector.off')}
            </span>
            {emailNotificationsEnabled && emailRecipients.length > 0 && (
              <div className={styles.recipientsList}>
                {emailRecipients.join(', ')}
              </div>
            )}
          </div>

          <div className={styles.settingRow}>
            <span className={styles.settingLabel}>
              {t('web.admin.visitorManagement.configuration.watchlistLevelSelector.systemLogging')}
            </span>
            <span className={styles.settingValue}>
            {isSystemLoggingEnabled ? t('web.admin.visitorManagement.configuration.watchlistLevelSelector.on') : t('web.admin.visitorManagement.configuration.watchlistLevelSelector.off')}
            </span>
          </div>

          <div className={styles.settingRow}>
            <span className={styles.settingLabel}>
              {t('web.admin.visitorManagement.configuration.watchlistLevelSelector.manualApproval')}
            </span>
            <span className={styles.settingValue}>
            {isManualApprovalEnabled ? t('web.admin.visitorManagement.configuration.watchlistLevelSelector.on') : t('web.admin.visitorManagement.configuration.watchlistLevelSelector.off')}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={cx(styles.watchlistLevelSelector, className)}>
      <Flex direction="row" gap={4} className={styles.cardsContainer}>
        {SEVERITY_ORDER.map(level => renderLevelCard(level))}
      </Flex>
    </div>
  );
}

